'use client';

import { createContext, type ReactNode, useContext } from 'react';
import { useStore } from 'zustand';

import type { AppStoreType, createAppBoundStore } from './zustand-store';

export type AppStoreApi = ReturnType<typeof createAppBoundStore>;

const AppStoreContext = createContext<AppStoreApi | undefined>(undefined);

interface AppProviderProps {
  children: ReactNode;
  store?: AppStoreApi;
}

// Setup following docs recommendation https://zustand.docs.pmnd.rs/guides/nextjs
export const ZustandStoreProvider = ({ children, store }: AppProviderProps) => {
  return (
    <AppStoreContext.Provider value={store}>
      {children}
    </AppStoreContext.Provider>
  );
};

// we already have inline selectors exported from store. Idk if we need this.
// Use for inline selectors (e.g., s => s.cart) to subscribe to specific state slices.
export function useAppStore<T>(selector: (store: AppStoreType) => T): T {
  const appStoreContext = useContext(AppStoreContext);

  if (!appStoreContext) {
    throw new Error(`useAppStore must be used within AppProvider`);
  }

  return useStore(appStoreContext, selector);
}

// Use for autogenerated selectors with autocomplete (e.g., .use.cart()) or accessing actions via getState().
export function useAppStoreInContext(): AppStoreApi {
  const store = useContext(AppStoreContext);
  if (!store) {
    throw new Error('useAppStoreContext must be used within AppStoreProvider');
  }
  return store;
}
